import { useEffect, useRef, useCallback } from 'react';
import { storeUserActivity } from '../api_client/recommendation_api';
import { UserActivityType, ResourceType } from '../types/activity_types';

// For logging/debugging
const DEBUG = false;
const logDebug = (...args: any[]) => DEBUG && console.log('[ScrollTracker]', ...args);

interface ScrollTrackerProps {
  resourceId: string;
  resourceType: ResourceType;
  derived_from_resource_id?: string; // Optional parameter for YT_SHARP items
}

interface BatchManager {
  items: Set<string>;
  batchItems: BatchItem[];
  timeout: number | null;
  lastFlush: number;
}

// Enhanced batch item structure to capture all necessary data at queue time
interface BatchItem {
  id: string;
  resourceType: ResourceType;
  derivedResourceId: string;
}

// Queue of items to be processed
const batchItems: BatchItem[] = [];

// Singleton batch manager to handle all scroll activities across components
// Using a module-level singleton ensures we batch across all component instances
const batchManager: BatchManager = {
  items: new Set<string>(), // Track IDs for deduplication
  batchItems: batchItems, // Reference to the batch items array
  timeout: null,
  lastFlush: 0
};

const BATCH_DELAY = 2000; // Base delay between batches
const MAX_BATCH_SIZE = 50; // Maximum number of items to process in one batch
const MIN_INTERVAL = 1000; // Minimum time between API calls

const processBatch = async () => {
  const now = Date.now();
  // If we've recently processed a batch, wait until minimum interval
  if (now - batchManager.lastFlush < MIN_INTERVAL) {
    return;
  }

  // Grab a slice of the batch items to process
  const itemsToProcess = batchManager.batchItems.slice(0, MAX_BATCH_SIZE);
  if (itemsToProcess.length === 0) return;

  // Group by resource type for more efficient processing
  const itemsByType = new Map<ResourceType, BatchItem[]>();
  
  // Process all batch items
  itemsToProcess.forEach(item => {
    if (!itemsByType.has(item.resourceType)) {
      itemsByType.set(item.resourceType, []);
    }
    itemsByType.get(item.resourceType)?.push(item);
    
    // Remove the processed ID from the tracking set
    batchManager.items.delete(item.id);
  });
  
  // Remove the processed items from the batch array
  batchManager.batchItems.splice(0, itemsToProcess.length);
  
  batchManager.lastFlush = now;
  logDebug(`Processing batch of ${itemsToProcess.length} items`);

  try {
    // Process each resource type group in parallel
    const promises: Promise<any>[] = [];
    
    itemsByType.forEach((items, type) => {
      // For each resource type, process all its items
      const typePromises = items.map(item => {
        // Process each item with its captured context
        return storeUserActivity(
          item.id, // document_id
          item.resourceType,
          UserActivityType.SCROLLED_PAST,
          item.derivedResourceId
        );
      });
      promises.push(...typePromises);
    });
    
    await Promise.all(promises);
    logDebug(`Successfully processed ${promises.length} activities`);
  } catch (error) {
    console.error('Error storing scroll activities:', error);
    
    // On error, add items back to the batch for retry
    itemsToProcess.forEach(item => {
      batchManager.items.add(item.id);
      batchManager.batchItems.push(item);
    });
  }

  // If there are remaining items, schedule next batch
  if (batchManager.items.size > 0) {
    batchManager.timeout = window.setTimeout(
      () => processBatch(),
      BATCH_DELAY
    );
    logDebug(`Scheduled next batch with ${batchManager.items.size} remaining items`);
  } else {
    batchManager.timeout = null;
    logDebug('No more items to process');
  }
};

export const useScrollTracker = ({ resourceId, resourceType, derived_from_resource_id }: ScrollTrackerProps) => {
  // Early validation
  if (!resourceId) {
    console.warn('useScrollTracker called with empty resourceId');
  }
  const elementRef = useRef<HTMLDivElement>(null);
  const hasBeenViewed = useRef(false);
  const hasBeenTracked = useRef(false);

  // Early initialization of scroll tracking refs

  const queueActivity = useCallback(() => {
    if (!hasBeenTracked.current) {
      hasBeenTracked.current = true;
      
      // Only track if we have a valid resourceId
      if (resourceId) {
        // Only add if not already in the batch
        if (!batchManager.items.has(resourceId)) {
          batchManager.items.add(resourceId);
          
          // Create a batch item that captures all the data we need at this moment
          // This prevents the context from being lost during async operations
          const batchItem: BatchItem = {
            id: resourceId,
            resourceType,
            derivedResourceId: derived_from_resource_id || ""
          };
          
          // Add the item to the batch queue
          batchManager.batchItems.push(batchItem);

        }
        logDebug(`Queued activity for resource: ${resourceId}`);

        // Start batch processing if not already started
        if (!batchManager.timeout) {
          batchManager.timeout = window.setTimeout(
            () => processBatch(),
            BATCH_DELAY
          );
          logDebug('Started batch processing timer');
        }
      }
    }
  }, [resourceId, resourceType, derived_from_resource_id]);

  useEffect(() => {
    const currentElement = elementRef.current;
    if (!currentElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            hasBeenViewed.current = true;
          } else if (
            hasBeenViewed.current &&
            !hasBeenTracked.current &&
            entry.boundingClientRect.top < 0
          ) {
            queueActivity();
          }
        });
      },
      {
        threshold: [0],
        rootMargin: '0px'
      }
    );

    observer.observe(currentElement);

    return () => {
      observer.disconnect();
      // If this item is queued but not yet processed, process it now
      if (resourceId && batchManager.items.has(resourceId)) {
        logDebug(`Processing queued item on unmount: ${resourceId}`);
        processBatch();
      }
    };
  }, [resourceId, resourceType, queueActivity]);

  return elementRef;
};
